/* ============================================
   ALnoor Theme — Collection Filters & Sort
   AJAX filtering, sorting, and view toggles
   ============================================ */

(function () {
  'use strict';

  const selectors = {
    section: '[data-section-type="collection"]',
    filterToggle: '[data-filter-toggle]',
    filterClose: '[data-filter-close]',
    filterOverlay: '[data-filter-overlay]',
    filters: '[data-filters]',
    filterForm: '[data-filter-form]',
    filterInput: '[data-filter-input]',
    filterRemove: '[data-filter-remove]',
    filterClear: '[data-filter-clear]',
    filterApply: '[data-filter-apply]',
    sortSelect: '[data-sort-select]',
    viewToggle: '[data-view-toggle]',
    productGrid: '[data-product-grid]',
    productsContainer: '[data-products-container]',
    productCount: '[data-product-count]'
  };

  class CollectionFilters {
    constructor() {
      this.section = document.querySelector(selectors.section);
      if (!this.section) return;

      this.sectionId = this.section.dataset.sectionId;
      this.filterDrawer = this.section.querySelector(selectors.filters);
      this.filterForm = this.section.querySelector(selectors.filterForm);
      this.sortSelect = this.section.querySelector(selectors.sortSelect);
      this.productsContainer = this.section.querySelector(selectors.productsContainer);
      this.releaseFocus = null;

      this.bindEvents();
    }

    bindEvents() {
      /* Filter toggle (open drawer on mobile) */
      this.section.querySelectorAll(selectors.filterToggle).forEach((btn) => {
        btn.addEventListener('click', () => this.openFilters());
      });

      /* Filter close */
      this.section.querySelectorAll(selectors.filterClose).forEach((btn) => {
        btn.addEventListener('click', () => this.closeFilters());
      });

      /* Overlay click */
      this.section.querySelectorAll(selectors.filterOverlay).forEach((overlay) => {
        overlay.addEventListener('click', () => this.closeFilters());
      });

      /* Escape key */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.filterDrawer && this.filterDrawer.classList.contains('is-open')) {
          this.closeFilters();
        }
      });

      /* Filter form submission (desktop auto-submit on checkbox change) */
      if (this.filterForm) {
        this.filterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.applyFilters();
        });

        /* Auto-submit on checkbox change for desktop */
        this.filterForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.addEventListener('change', () => {
            if (window.innerWidth >= 990) {
              this.applyFilters();
            }
          });
        });

        /* Debounce price range inputs */
        this.filterForm.querySelectorAll('input[type="number"]').forEach((input) => {
          input.addEventListener('change', debounce(() => {
            this.applyFilters();
          }, 500));
        });
      }

      /* Filter remove links (active tags) */
      this.section.addEventListener('click', (e) => {
        const removeLink = e.target.closest(selectors.filterRemove);
        if (removeLink) {
          e.preventDefault();
          this.fetchAndRender(removeLink.href);
        }

        const clearLink = e.target.closest(selectors.filterClear);
        if (clearLink) {
          e.preventDefault();
          this.fetchAndRender(clearLink.href);
        }
      });

      /* Sort select */
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.applySort();
        });
      }

      /* View toggle (grid / list) */
      this.section.querySelectorAll(selectors.viewToggle).forEach((btn) => {
        btn.addEventListener('click', () => {
          this.toggleView(btn.dataset.viewToggle);
        });
      });
    }

    /* --- Open filter drawer --- */
    openFilters() {
      if (!this.filterDrawer) return;
      this.filterDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      const toggle = this.section.querySelector(selectors.filterToggle);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');

      /* Trap focus */
      const inner = this.filterDrawer.querySelector('.collection-filters__inner');
      if (inner && typeof trapFocus === 'function') {
        const closeBtn = inner.querySelector(selectors.filterClose);
        this.releaseFocus = trapFocus(inner, closeBtn);
      }
    }

    /* --- Close filter drawer --- */
    closeFilters() {
      if (!this.filterDrawer) return;
      this.filterDrawer.classList.remove('is-open');
      document.body.style.overflow = '';

      const toggle = this.section.querySelector(selectors.filterToggle);
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }

      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
    }

    /* --- Build filter URL from form --- */
    buildFilterUrl() {
      const formData = new FormData(this.filterForm);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value !== '' && value !== undefined) {
          params.append(key, value);
        }
      }

      /* Add sort if present */
      if (this.sortSelect && this.sortSelect.value) {
        params.set('sort_by', this.sortSelect.value);
      }

      const baseUrl = window.location.pathname;
      const paramString = params.toString();
      return paramString ? `${baseUrl}?${paramString}` : baseUrl;
    }

    /* --- Apply filters --- */
    applyFilters() {
      const url = this.buildFilterUrl();
      this.fetchAndRender(url);
      this.closeFilters();
    }

    /* --- Apply sort --- */
    applySort() {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.sortSelect.value);
      this.fetchAndRender(url.toString());
    }

    /* --- Fetch page and render --- */
    async fetchAndRender(url) {
      this.setLoading(true);

      try {
        const separator = url.includes('?') ? '&' : '?';
        const fetchUrl = `${url}${separator}section_id=${this.sectionId}`;
        const response = await fetch(fetchUrl);

        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        /* Update product grid */
        const newProducts = doc.querySelector(selectors.productsContainer);
        if (newProducts && this.productsContainer) {
          this.productsContainer.innerHTML = newProducts.innerHTML;
        }

        /* Update filter sidebar (active tags, counts) */
        const newFilters = doc.querySelector(selectors.filters);
        if (newFilters && this.filterDrawer) {
          const newInner = newFilters.querySelector('.collection-filters__inner');
          const currentInner = this.filterDrawer.querySelector('.collection-filters__inner');
          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
          }
          /* Rebind filter form */
          this.filterForm = this.filterDrawer.querySelector(selectors.filterForm);
          this.rebindFilterEvents();
        }

        /* Update product count */
        const newCount = doc.querySelector(selectors.productCount);
        const currentCount = this.section.querySelector(selectors.productCount);
        if (newCount && currentCount) {
          currentCount.innerHTML = newCount.innerHTML;
        }

        /* Update toolbar (filter count badge) */
        const newToggle = doc.querySelector(selectors.filterToggle);
        const currentToggle = this.section.querySelector(selectors.filterToggle);
        if (newToggle && currentToggle) {
          currentToggle.innerHTML = newToggle.innerHTML;
        }

        /* Update URL without page reload */
        window.history.pushState({}, '', url);

        /* Re-init scroll animations */
        if (typeof initScrollAnimations === 'function') {
          initScrollAnimations();
        }

      } catch (error) {
        console.error('Collection filter error:', error);
        /* Fall back to standard navigation */
        window.location.href = url;
      } finally {
        this.setLoading(false);
      }
    }

    /* --- Rebind events after HTML replacement --- */
    rebindFilterEvents() {
      if (!this.filterForm) return;

      this.filterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.applyFilters();
      });

      this.filterForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.addEventListener('change', () => {
          if (window.innerWidth >= 990) {
            this.applyFilters();
          }
        });
      });

      this.filterForm.querySelectorAll('input[type="number"]').forEach((input) => {
        input.addEventListener('change', debounce(() => {
          this.applyFilters();
        }, 500));
      });

      /* Close button */
      this.filterDrawer.querySelectorAll(selectors.filterClose).forEach((btn) => {
        btn.addEventListener('click', () => this.closeFilters());
      });
    }

    /* --- Toggle grid / list view --- */
    toggleView(view) {
      const grid = this.section.querySelector(selectors.productGrid);
      if (!grid) return;

      const buttons = this.section.querySelectorAll(selectors.viewToggle);
      buttons.forEach((btn) => {
        const isActive = btn.dataset.viewToggle === view;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (view === 'list') {
        grid.classList.add('collection-grid--list');
        grid.classList.remove('grid--2', 'grid--3', 'grid--4');
      } else {
        grid.classList.remove('collection-grid--list');
        const columns = grid.dataset.columns || '4';
        grid.classList.add(`grid--${columns}`);
      }
    }

    /* --- Loading state --- */
    setLoading(loading) {
      if (this.productsContainer) {
        this.productsContainer.style.opacity = loading ? '0.5' : '';
        this.productsContainer.style.pointerEvents = loading ? 'none' : '';
        this.productsContainer.style.transition = 'opacity 0.2s ease';
      }
    }
  }

  /* --- Handle browser back/forward --- */
  window.addEventListener('popstate', () => {
    window.location.reload();
  });

  /* --- Init on DOM ready --- */
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionFilters();
  });

  /* --- Re-init on Shopify section reload --- */
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector(selectors.section)) {
      new CollectionFilters();
    }
  });
})();
